import { useEffect, useState } from "react";
import { PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";

const STATUSES = ["new", "assigned", "contact_attempted", "contacted", "follow_up_required", "resolved", "closed"];
const STATUS_STYLE = {
  new: "bg-amber-50 text-amber-700", assigned: "bg-sky-50 text-sky-700", contact_attempted: "bg-sky-50 text-sky-700",
  contacted: "bg-cobalt/10 text-cobalt", follow_up_required: "bg-amber-100 text-amber-800",
  resolved: "bg-emerald-50 text-emerald-700", closed: "bg-slate-100 text-slate-500",
};

export default function CallRequests() {
  const [records, setRecords] = useState(null);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState("");

  const load = () => api.get("/admin/call-requests").then((response) => setRecords(response.data.call_requests)).catch(() => setRecords([]));
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/call-requests/${id}`, patch);
      toast.success("Updated.");
      setNote("");
      await load();
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-call-requests" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-ink">Call requests</h1>
        <p className="mt-1 text-sm text-slatey">Learners who asked for human help, with the OpenDegree context attached.</p>
      </div>

      {!records ? <p className="text-sm text-slatey">Loading…</p> : (
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          <div className="space-y-3">
            {records.map((record) => (
              <button key={record.id} onClick={() => { setSelected(record); setAssignee(record.assigned_to || ""); }} data-testid={`call-request-${record.id}`}
                className={`flex w-full flex-wrap items-center justify-between gap-3 rounded-3xl border bg-white p-4 text-left transition-colors ${selected?.id === record.id ? "border-cobalt" : "border-slate-200 hover:border-cobalt/40"}`}>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-cobalt/10 text-cobalt"><PhoneCall className="h-4 w-4" /></div>
                  <div>
                    <p className="font-bold text-ink">{record.name} <span className="ml-1 text-xs font-semibold text-slatey">{record.reason_label}</span></p>
                    <p className="text-xs text-slatey">{record.context?.opportunity_title ? `${record.context.opportunity_title} · ` : ""}{record.created_at?.slice(0, 16).replace("T", " ")}</p>
                  </div>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[record.status]}`} data-testid={`call-request-status-${record.id}`}>{record.status.replaceAll("_", " ")}</span>
              </button>
            ))}
            {records.length === 0 && <p className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slatey">No call requests yet.</p>}
          </div>

          {selected && (
            <aside className="h-fit rounded-3xl border border-slate-200 bg-white p-5" data-testid="call-request-detail">
              <h2 className="font-display text-xl font-bold text-ink">{selected.name}</h2>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-3"><dt className="text-slatey">Reason</dt><dd className="font-semibold text-ink">{selected.reason_label}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slatey">Email</dt><dd className="font-semibold text-ink">{selected.email || "—"}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slatey">Mobile</dt><dd className="font-semibold text-ink">{selected.mobile || "—"}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slatey">Prefers</dt><dd className="font-semibold capitalize text-ink">{selected.preferred_contact_method}{selected.preferred_time ? ` · ${selected.preferred_time}` : ""}</dd></div>
                {selected.context?.opportunity_title && <div className="flex justify-between gap-3"><dt className="text-slatey">Opportunity</dt><dd className="text-right font-semibold text-ink">{selected.context.opportunity_title}</dd></div>}
                {selected.context?.provider && <div className="flex justify-between gap-3"><dt className="text-slatey">Provider</dt><dd className="font-semibold text-ink">{selected.context.provider}</dd></div>}
                {selected.application && <div className="flex justify-between gap-3"><dt className="text-slatey">Application</dt><dd className="font-semibold text-ink">{selected.application.reference || selected.application.id} · {selected.application.status?.replaceAll("_", " ")}</dd></div>}
                {selected.message && <div><dt className="text-slatey">Message</dt><dd className="mt-1 rounded-2xl bg-surface p-3 text-ink">{selected.message}</dd></div>}
              </dl>

              <div className="mt-4">
                <label className="mb-1 block text-xs font-bold text-slatey">Status</label>
                <select value={selected.status} onChange={(e) => update(selected.id, { status: e.target.value })} data-testid="call-request-status-select"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cobalt">
                  {STATUSES.map((status) => <option key={status} value={status}>{status.replaceAll("_", " ")}</option>)}
                </select>
              </div>
              <div className="mt-3 flex gap-2">
                <input value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="call-request-assignee" placeholder="Assign to team member" className="w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-cobalt" />
                <button onClick={() => update(selected.id, { assigned_to: assignee })} data-testid="call-request-assign" className="rounded-full border border-slate-300 px-4 py-2.5 text-xs font-bold text-ink">Assign</button>
              </div>
              <div className="mt-3 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="call-request-note" placeholder="Add a note" className="w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-cobalt" />
                <button onClick={() => update(selected.id, { note })} disabled={!note.trim()} data-testid="call-request-note-add" className="rounded-full border border-slate-300 px-4 py-2.5 text-xs font-bold text-ink disabled:opacity-50">Add</button>
              </div>
              {(selected.notes || []).length > 0 && (
                <ul className="mt-3 space-y-2">
                  {selected.notes.map((entry, index) => <li key={index} className="rounded-2xl bg-surface px-3 py-2 text-xs text-slatey"><strong className="text-ink">{entry.by}</strong> · {entry.at?.slice(0, 16).replace("T", " ")}<br />{entry.note}</li>)}
                </ul>
              )}
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
